
import {defineComponent} from "vue";
import {CodeDescriptionRestDto,} from "@/api/pharma-cpc-mgmt/models";
import {useToast} from "vue-toastification";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import BaseOffcanvasButtonClose from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonClose.vue";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import {PopupAssociationRestDto} from "@/api/pharma-cms-popup/models";
import {PopupAssociationTypeEnum} from "@/api/enums/popup-association-type-enum";
import {PopupAssociationRestService} from "@/services/rest/cms-popup/PopupAssociationRestService";
import {SnippetRestService} from "@/services/rest/cms-snippet/SnippetRestService";
import {PagedSnippetInSlotOverviewRestDto, SnippetInSlotOverviewRestDto} from "@/api/pharma-cms-snippet/models";
import {ContentPageRestService} from "@/services/rest/cms-content-page/ContentPageRestService";
import CpAbstractsOverview from "@/components/layouts/content-pages/overview/CpAbstractsOverview.vue";
import {TargetPageInfoRestDto} from "@/api/pharma-cms-content-page/models";
import {AutoSaveFeedbackStatusEnum} from "@/models/enum/AutoSaveFeedbackStatusEnum";
import AutoSaveFeedback from "@/components/UI/AutoSaveFeedback.vue";
import {PageTypeEnum} from "@/api/enums/page-type-enum";
import {AbilityContext} from "@/context/AbilityContext";

export default defineComponent({
  name: "Association",
  emits: ['edit'],
  components: {
    AutoSaveFeedback,
    CpAbstractsOverview,
    BaseOffcanvasButtonClose,
    AlertError2
  },
  props: {
    popupId: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: false,
    },
    mode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      createAssociationType: null as PopupAssociationTypeEnum | null,
      associationTypeOptions: [] as CodeDescriptionRestDto[],
      association: {} as PopupAssociationRestDto,
      autoSaveFeedbackStatus: AutoSaveFeedbackStatusEnum.NONE,
      pageTypeOptions: [] as CodeDescriptionRestDto[],
      snippetOptions: [] as CodeDescriptionRestDto[],
      // TODO: what do we do with languages?  (Nothing for now)
      enableAltLangPages: false,
      linkedPage: null as TargetPageInfoRestDto | null,
      toaster: useToast(),
      pageUI: UIStateDto.createWithDefaults(),
    }
  },
  mounted() {
    this.reloadContent();
  },
  computed: {
    PageDetailModeEnum() {
      return PageDetailModeEnum;
    },
    PopupAssociationTypeEnum() {
      return PopupAssociationTypeEnum;
    },
    AutoSaveFeedbackStatusEnum() {
      return AutoSaveFeedbackStatusEnum;
    },
    associationTypeDescription(): string {
      if (this.association.association_type_code) {
        const associationTypeCodeDescription =
          this.associationTypeOptions.find(value => value.code === this.association.association_type_code);
        if (associationTypeCodeDescription) {
          return associationTypeCodeDescription.description;
        }
      }

      return '';
    }
  },
  methods: {
    async submitData() {
      this.pageUI
        .clearError()
        .setNotReady();
      this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.BUSY;

      try {
        if (this.mode === PageDetailModeEnum.NEW) {
          if (this.association.association_type_code === PopupAssociationTypeEnum.NAMED_PAGE) {
            this.association.page_ref = {
              page_type: PageTypeEnum.HOME_PAGE_CONTENT,
            };
          }
          await PopupAssociationRestService.getInstance().addAssociation(this.association, this.popupId);
          this.$emit('edit');
          this.closeCurrentOffCanvas();
          return;
        }

        await PopupAssociationRestService.getInstance().updateAssociation(this.id, this.association);
        this.$emit('edit');
        await this.afterSave();
        this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.NONE;
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },
    async reloadContent() {
      this.pageUI
        .clearError()
        .setNotReady();
      try {
        let options = await EnumRestService.getInstance()
          .getCmsEnumValuesByClassname(EnumClassNameCmsEnum.POPUP_ASSOCIATION_TYPE);
        if (!AbilityContext.isAuthorizedForFeature('CONTENT_PAGE_SNIPPETS')) {
          options = options
            .filter((codeDescription: CodeDescriptionRestDto) => codeDescription.code !== PopupAssociationTypeEnum.SNIPPET);
        }
        // TODO: remove map description to homepage as soon as the implementation for the named page is ready
        this.associationTypeOptions =
          options
            .map((codeDescription: CodeDescriptionRestDto) => {
              if (codeDescription.code === PopupAssociationTypeEnum.NAMED_PAGE) {
                codeDescription.description = this.$t('homepage');
              }
              return codeDescription;
            });

        const allPageTypeOptions =
          await ContentPageRestService.getInstance().findContentPageTypes(false);
        this.pageTypeOptions = allPageTypeOptions
          .filter((value: CodeDescriptionRestDto) => (value.code !== 'CUSTOM_CONTENT_PAGE'));

        if (AbilityContext.isAuthorizedForFeature('CONTENT_PAGE_SNIPPETS')) {
          const allSnippets: PagedSnippetInSlotOverviewRestDto = await SnippetRestService.getInstance().findSnippetsInSlot();
          this.snippetOptions = allSnippets.results.map((snippet: SnippetInSlotOverviewRestDto) => {
            return {
              code: snippet.id,
              description: snippet.name
            }
          });
        }

        if (this.mode === PageDetailModeEnum.NEW) {
          this.createAssociationType = null;
          this.association = {};
        } else {
          this.association = await PopupAssociationRestService.getInstance().getAssociation(this.id);
          if (this.association.page_ref) {
            this.linkedPage = await ContentPageRestService.getInstance().getContentPage(this.association.page_ref.page_id);
          }
        }
        this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.NONE;
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },
    async afterSave() {
      this.toaster.success(this.$t('savedSuccessfully'));
      await this.reloadContent();
    },
    onAssociationChanged(): void {
      this.autoSaveFeedbackStatus = AutoSaveFeedbackStatusEnum.UNSAVED_CHANGES;
    },
    async setPageId(id: string): Promise<void> {
      this.linkedPage = await ContentPageRestService.getInstance().getContentPage(id);
      this.association.page_ref = {
        page_id: id,
      };
      this.onAssociationChanged();
    },
    clearPageId(): void {
      this.linkedPage = null;
      this.association.page_ref = null;
      this.onAssociationChanged();
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
        .create()
        .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .catch(exceptions, true);
    },
    closeCurrentOffCanvas(): void {
      // Wait one tick to ensure DOM is updated after the form is submitted
      this.$nextTick(() => {
        const closeButton = this.$refs.closeButton as any;
        if (closeButton && closeButton.$el) {
          closeButton.$el.click();
        }
      });
    }
  },
});
